import classnames from "classnames";
import moment from "moment";
import { useRouter } from "next/router";
import React from "react";

import {
  PriceCalendarOptions,
  PriceCalendarSelectionMode,
} from "shared/lib/priceCalendar";

import Calendar from "@components/SearchForm/Calendar/Calendar";
import type { PriceCalendarCache } from "@components/SearchForm/Calendar/CalendarMonth";
import DatePicker from "@components/SearchForm/Calendar/DatePicker";
import FormattedMessage from "@components/UI/FormattedMessage";
import Modal from "@components/UI/Modal";

import { isIframe } from "@lib/iframe";

interface DatesRowProps {
  flightTypeSearch: string;
  departureDate: string;
  returnDate: string;
  setDate: (value: string, type: string) => void;
  blockSearchDaysBefore: number;
  blockSearchDaysAfter?: number;
  priceCalendar?: PriceCalendarOptions;
}

const DISABLE_DATES_AFTER_DAYS = 361;
const EXPORT_HTML_PACKAGE = Boolean(process.env.NEXT_PUBLIC_EXPORT_PACKAGE);
const CALENDAR_INITIAL_FOCUS_SELECTOR =
  ".header-search-form-calendar-initial-focus";

export default function DatesRow({
  flightTypeSearch,
  departureDate,
  returnDate,
  setDate,
  blockSearchDaysBefore,
  blockSearchDaysAfter,
  priceCalendar,
}: DatesRowProps) {
  const router = useRouter();
  const [showRangeCalendar, setShowRangeCalendar] = React.useState(false);
  const priceCalendarCache = React.useRef<PriceCalendarCache>({
    outbound: {},
    returns: {},
  });

  const isHotelSearchForm =
    router.query &&
    router.query.typeSearch &&
    router.query.typeSearch === "hotels";

  const iframe = isIframe(router.route);

  const disabledTillDate = moment().add(blockSearchDaysBefore, "days").toDate();
  let disabledAfterDate = moment()
    .add(blockSearchDaysAfter || DISABLE_DATES_AFTER_DAYS, "days")
    .toDate();

  if (isHotelSearchForm) {
    disabledAfterDate = blockSearchDaysAfter
      ? moment().add(blockSearchDaysAfter, "days").toDate()
      : undefined;
  }

  const rangeSelectionEnabled =
    flightTypeSearch === "RETURN" && !isHotelSearchForm;
  const getCalendarOpenHandler = () =>
    rangeSelectionEnabled ? () => setShowRangeCalendar(true) : undefined;
  const setDepartureDate = (value: string) => setDate(value, "DEPARTURE");
  const setReturnDate = (value: string) => setDate(value, "RETURN");
  const closeRangeCalendar = () => {
    if (departureDate && !returnDate) {
      setReturnDate(departureDate);
    }

    setShowRangeCalendar(false);
  };

  return (
    <div
      className={classnames(
        "header-search-form-desktop-line header-search-form-desktop-line--half",
        {
          iframe,
        }
      )}
    >
      <div
        className={classnames("flex-1", {
          iframe,
        })}
        id="departure_date-container"
      >
        <div
          className={classnames("header-search-form-inner-field", {
            iframe,
          })}
        >
          <span className="header-search-form-inner-field-label">
            {isHotelSearchForm ? (
              <FormattedMessage id="SearchForm.hotels.checkInDate" />
            ) : (
              <span id="GOL_package-textStorage-SearchForm.departureDate">
                <FormattedMessage id="SearchForm.departureDate" />
              </span>
            )}
          </span>
          <DatePicker
            testId="departure_date"
            typeSearch="DEPARTURE"
            date={departureDate}
            onChange={setDate}
            disabledTillDate={disabledTillDate}
            disabledAfterDate={disabledAfterDate}
            priceCalendar={priceCalendar}
            rangeSelectionEnabled={rangeSelectionEnabled}
            departureDate={departureDate}
            returnDate={returnDate}
            onDepartureChange={setDepartureDate}
            onReturnChange={setReturnDate}
            onOpenCalendar={getCalendarOpenHandler()}
          />
        </div>
      </div>
      <div
        className={classnames("flex-1", {
          iframe,
        })}
        id="return_date-container"
      >
        {(flightTypeSearch === "RETURN" || isHotelSearchForm) && (
          <div
            className={classnames("header-search-form-inner-field", {
              iframe,
            })}
          >
            <span className="header-search-form-inner-field-label">
              {isHotelSearchForm ? (
                <FormattedMessage id="SearchForm.hotels.checkOutDate" />
              ) : (
                <span id="GOL_package-textStorage-SearchForm.returnDate">
                  <FormattedMessage id="SearchForm.returnDate" />
                </span>
              )}
            </span>
            <DatePicker
              testId="return_date"
              typeSearch="RETURN"
              date={returnDate}
              onChange={setDate}
              disabledTillDate={
                rangeSelectionEnabled
                  ? disabledTillDate
                  : departureDate
                  ? new Date(departureDate)
                  : disabledTillDate
              }
              disabledAfterDate={disabledAfterDate}
              priceCalendar={priceCalendar}
              rangeSelectionEnabled={rangeSelectionEnabled}
              departureDate={departureDate}
              returnDate={returnDate}
              onDepartureChange={setDepartureDate}
              onReturnChange={setReturnDate}
              onOpenCalendar={getCalendarOpenHandler()}
            />
          </div>
        )}
      </div>
      {rangeSelectionEnabled && (
        <>
          <Modal
            isActive={showRangeCalendar}
            onClose={closeRangeCalendar}
            initialFocusSelector={CALENDAR_INITIAL_FOCUS_SELECTOR}
            innerClassName="header-search-form-calendar-modal"
          >
            <Calendar
              close={closeRangeCalendar}
              date={departureDate}
              onChange={setDate}
              typeSearch="DEPARTURE"
              disabledTillDate={disabledTillDate}
              disabledAfterDate={disabledAfterDate}
              priceCalendar={priceCalendar}
              rangeSelectionEnabled={rangeSelectionEnabled}
              departureDate={departureDate}
              returnDate={returnDate}
              onDepartureChange={setDepartureDate}
              onReturnChange={setReturnDate}
              initialSelectionMode={PriceCalendarSelectionMode.OUTBOUND}
              priceCalendarCache={priceCalendarCache}
            />
          </Modal>
          {EXPORT_HTML_PACKAGE && (
            <Calendar
              close={closeRangeCalendar}
              date={departureDate}
              onChange={setDate}
              typeSearch="DEPARTURE"
              disabledTillDate={disabledTillDate}
              disabledAfterDate={disabledAfterDate}
              priceCalendar={priceCalendar}
              rangeSelectionEnabled={rangeSelectionEnabled}
              departureDate={departureDate}
              returnDate={returnDate}
              onDepartureChange={setDepartureDate}
              onReturnChange={setReturnDate}
              initialSelectionMode={PriceCalendarSelectionMode.OUTBOUND}
              priceCalendarCache={priceCalendarCache}
            />
          )}
        </>
      )}
      <div
        className={classnames("", {
          iframe,
        })}
      />
    </div>
  );
}
